/* Marquee: clones the track to cover the viewport, paces the loop from the
   speed setting, and (pointer devices) slides the whole element sideways as
   the section crosses the viewport. Behaviour matches the measured reference:
   duration = (33 - speed) * min(2.5, ceil(track/viewport)) seconds; parallax
   start offset = parallax * 100 / (1 + parallax) percent.

   The whole module is guarded, not just the define: two scrolling-text
   sections on one page emit this script tag twice, and each tag executes — a
   bare top-level `class` would throw on redeclaration before any define guard
   runs. */
if (!customElements.get('marquee-element')) {
  customElements.define('marquee-element', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('.marquee');
      if (!this.track) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const parentWidth = this.getBoundingClientRect().width;
      const trackWidth = this.track.getBoundingClientRect().width;
      if (!trackWidth) return;

      const copies = Math.ceil(parentWidth / trackWidth) + 2;
      for (let i = 0; i < copies; i += 1) {
        const clone = this.track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('button, [href]').forEach((el) => el.setAttribute('tabindex', '-1'));
        this.appendChild(clone);
      }

      const speed = parseInt(this.dataset.speed, 10) || 16;
      const stretch = Math.min(2.5, Math.ceil(trackWidth / parentWidth));
      this.style.setProperty('--marquee-duration', `${(33 - speed) * stretch}s`);
      this.classList.add('is-ready');

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => this.classList.toggle('is-paused', !entry.isIntersecting));
        },
        { rootMargin: '0px 0px 50px 0px' }
      );
      observer.observe(this);

      const factor = parseFloat(this.dataset.parallax);
      if (factor && window.matchMedia('(hover: hover)').matches) this.parallax(factor);
    }

    parallax(factor) {
      const start = ((this.dataset.direction === 'right' ? -1 : 1) * factor * 100) / (1 + factor);
      let queued = false;
      const place = () => {
        queued = false;
        const rect = this.getBoundingClientRect();
        const span = window.innerHeight + rect.height;
        const progress = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / span));
        this.style.transform = `translateX(${start * (1 - progress)}%)`;
      };
      const schedule = () => {
        if (!queued) {
          queued = true;
          requestAnimationFrame(place);
        }
      };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      place();
    }
  });
}
